import { fetcherConfig } from '../../lib/api-fetcher';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

import { FormField, Input } from '../../components/ui/FormField';
import { Button } from '../../components/ui/Button';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Stack } from '../../components/layout/Stack';
import { createAuthResource } from '@repo/api-client';

const authResource = createAuthResource(fetcherConfig);

const forgotPasswordFormConfig = defineFormPage({
  resource: 'auth-forgot-password',
  defaultValues: { email: '' },
  schema: z.object({
    email: z.string().email('Enter a valid email'),
  }),
});

export const Route = createFileRoute('/_public/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  usePageTitle('Forgot Password');
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  const { form, isSubmitting, fieldError: fe } = useFormPage(
    forgotPasswordFormConfig,
    { mutateAsync: async (v) => v },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.setErrorMap({ onSubmit: undefined });
    setSubmitError(undefined);
    await form.handleSubmit();
    if (!form.state.canSubmit) return;
    try {
      await authResource.initiatePasswordReset({ email: form.getFieldValue('email') });
      setSuccess(true);
    } catch (err: unknown) {
      console.error('Failed to initiate password reset:', err);
      let msg = 'Failed to initiate password reset';
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const obj = err as Record<string, unknown>;
        if ('message' in obj && typeof obj.message === 'string') {
          msg = obj.message;
        } else if ('error' in obj && typeof obj.error === 'string') {
          msg = obj.error;
        }
      }
      setSubmitError(msg);
    }
  };

  if (success) {
    return (
      <AuthLayout title="Check your email">
        <Stack gap="6" className="items-center text-center">
          <div className="w-16 h-16 bg-success-50 rounded-full flex items-center justify-center mb-2">
            <CheckCircle2 className="w-8 h-8 text-success-500" />
          </div>
          <p className="text-sm text-surface-600">
            We have sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: '/login' })}
            className="inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset Password">
      <div className="mb-6">
        <p className="text-sm text-surface-600">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }: { value: any }) => {
                const res = z.string().email('Enter a valid email').safeParse(value);
                return res.success ? undefined : res.error.issues[0].message;
              }
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="Email" required error={error} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={!!error}
                    disabled={isSubmitting}
                    placeholder="you@company.com"
                    startIcon={<Mail className="w-5 h-5" />}
                  />
                </FormField>
              );
            }}
          </form.Field>

          {submitError && (
            <p className="text-xs text-danger-text text-center" role="alert">
              {submitError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isSubmitting}
            className="w-full"
          >
            Send reset link
          </Button>

          <div className="flex justify-center mt-4">
            <Link to="/login" className="text-sm text-surface-500 hover:text-surface-700 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </Stack>
      </form>
    </AuthLayout>
  );
}
